import { queryOptions } from "@tanstack/react-query"

import { apiKey } from "@/lib/api/changes"
import { apiFetch } from "@/lib/api/client"
import type { Country, CountryOption } from "@/types/country"

type CountryList = { items: Country[] }

/** ISO 3166 countries, from Laravel's `App\Enums\Country`. Never stale. */
export const countryQueries = {
  list: () =>
    queryOptions({
      queryKey: apiKey("/countries"),
      queryFn: async ({ signal }): Promise<CountryList> => {
        const { data } = await apiFetch<{ data: Country[] }>("/countries", {
          anonymous: true,
          signal,
        })

        return { items: data }
      },
      staleTime: Number.POSITIVE_INFINITY,
    }),
}

/** The country's name for a code, or the code while the list loads. */
export function countryName(items: Country[] = [], code: string | null) {
  return code === null
    ? null
    : (items.find((country) => country.code === code)?.name ?? code)
}

/** What a national of the country is called, or the code while loading. */
export function nationalityName(items: Country[] = [], code: string | null) {
  return code === null
    ? null
    : (items.find((country) => country.code === code)?.nationality ?? code)
}

const commonCountries = ["GB", "IE", "IN", "PL", "PK", "RO", "NG", "ZA", "US"]

/** The handful that answer most of the time, then everything else. */
function commonFirst<TOption extends { value: string; label: string }>(
  options: TOption[],
  common: string[],
  keyOf: (option: TOption) => string
): TOption[] {
  const rank = new Map(common.map((value, index) => [value, index]))

  return [...options].sort((left, right) => {
    const leftRank = rank.get(keyOf(left)) ?? Number.MAX_SAFE_INTEGER
    const rightRank = rank.get(keyOf(right)) ?? Number.MAX_SAFE_INTEGER

    return leftRank === rightRank
      ? left.label.localeCompare(right.label)
      : leftRank - rightRank
  })
}

export function countryOptions(items: Country[] = []): CountryOption[] {
  return commonFirst(
    items.map((country) => ({
      value: country.code,
      label: country.name,
      flag: country.code,
    })),
    commonCountries,
    (option) => option.value
  )
}

/** Nationalities, valued by country code as Laravel stores them. */
export function nationalityOptions(items: Country[] = []): CountryOption[] {
  return commonFirst(
    items.flatMap((country) =>
      country.nationality
        ? [
            {
              value: country.code,
              label: country.nationality,
              flag: country.code,
            },
          ]
        : []
    ),
    commonCountries,
    (option) => option.value
  )
}
